import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Group } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useLeadStatusRepository, useLeadTypeRepository } from '@ayocore/erp/crm/client/rpc';
import { useRepositoryFilter } from '@ayocore/platform/client/components/RepositoryTable/useRepositoryFilter';
import type { LeadRepository } from '@ayocore/erp/crm/server/repositories/LeadRepository';

interface StatusOption {
  id: number;
  name: string;
  color: string | null;
}

/** URL-сериализация: множество выбранных id → 'id1,id2'. */
function parseFromUrl(raw: string | null): number[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => Number.isFinite(n) && n > 0);
}

function serializeForUrl(ids: number[]): string | null {
  return ids.length === 0 ? null : ids.join(',');
}

/**
 * Мульти-фильтр по статусам лида в виде Button.Group с бейджами-счётчиками.
 *
 * Список опций реактивно зависит от соседнего фильтра «Тип лида»
 * (`lead_type` slug в URL):
 *  - тип выбран     — рендерим кнопку на каждый статус выбранной воронки;
 *  - тип не выбран  — фильтр не рендерится (null), не занимает место;
 *  - активна ровно одна воронка — берём её без выбора: соседний селектор в
 *    этом случае скрыт (выбирать не из чего), и ждать выбора было бы нечего.
 *
 * Счётчики реактивно учитывают остальные фильтры таблицы (тип, Мои/Партнёр/
 * Ответственный и т.д.) — через `countByStatus()` на отфильтрованном репозитории.
 * Один SQL-запрос (GROUP BY lead_status_id) на все кнопки.
 *
 * URL: `status=1,3,5`. Одиночное значение (например `status=5`) парсится корректно
 * — обратная совместимость со старым однозначным селектом.
 */
export const FilterByStatus = useRepositoryFilter<LeadRepository>(
  'status',
  ({ filter, disabled, fromUrl, toUrl, subscribe, readSiblingUrl, getFilteredRepository }) => {
    const leadStatusRepository = useLeadStatusRepository();
    const leadTypeRepository = useLeadTypeRepository();

    const [leadTypeSlug, setLeadTypeSlug] = useState<string | null>(() =>
      readSiblingUrl('lead_type')
    );
    /** Слаг единственной активной воронки — если она одна. */
    const [soloTypeSlug, setSoloTypeSlug] = useState<string | null>(null);
    const [statuses, setStatuses] = useState<StatusOption[]>([]);
    const [selected, setSelected] = useState<number[]>([]);
    const [counts, setCounts] = useState<Record<number, number>>({});

    const selectedSet = useMemo(() => new Set(selected), [selected]);

    // ── Единственная активная воронка ───────────────────────────────────────
    // Когда она одна, селектор типа не рендерится, и ждать от него значения
    // бессмысленно — берём воронку сами, иначе на месте кнопок статусов
    // осталась бы пустота.
    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const types = await leadTypeRepository.where('is_active', true).limit(50).fetch();
          if (cancelled) return;
          setSoloTypeSlug(types.length === 1 ? (types[0].slug ?? null) : null);
        } catch (error) {
          logger.warn('FilterByStatus: не удалось загрузить активные типы', error);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [leadTypeRepository]);

    /** Воронка, по которой показываем статусы: выбранная либо единственная. */
    const effectiveTypeSlug = leadTypeSlug ?? soloTypeSlug;

    // ── Подписка на смену типа лида ─────────────────────────────────────────
    useEffect(() => {
      const unsubscribe = subscribe(() => {
        const next = readSiblingUrl('lead_type');
        setLeadTypeSlug(prev => (prev === next ? prev : next));
      });
      return unsubscribe;
    }, [subscribe, readSiblingUrl]);

    // ── Загрузка статусов при смене типа ────────────────────────────────────
    useEffect(() => {
      if (!effectiveTypeSlug) {
        setStatuses([]);
        // Сбрасываем выбор, если был
        setSelected(prev => {
          if (prev.length === 0) return prev;
          filter(null);
          toUrl(null);
          return [];
        });
        return;
      }

      let cancelled = false;
      (async () => {
        try {
          const data = await leadStatusRepository
            .filterByTypeSlug(effectiveTypeSlug)
            .orderBy('sort_order', 'asc')
            .limit(100)
            .fetch();
          if (cancelled) return;
          const opts: StatusOption[] = data.map(s => ({
            id: s.id,
            name: s.name || 'Без названия',
            color: s.color ?? null,
          }));
          setStatuses(opts);

          // Если ранее выбранные статусы не относятся к новой воронке — отфильтровываем
          setSelected(prev => {
            const validIds = new Set(opts.map(o => o.id));
            const survived = prev.filter(id => validIds.has(id));
            if (survived.length === prev.length) return prev;
            filter(survived.length > 0 ? repo => repo.filterByStatusAny(survived) : null);
            toUrl(serializeForUrl(survived));
            return survived;
          });
        } catch (error) {
          if (!cancelled) logger.error('FilterByStatus: load statuses failed', error);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [effectiveTypeSlug, leadStatusRepository, filter, toUrl]);

    // ── Подсчёт лидов по статусам ───────────────────────────────────────────
    const reloadCounts = useCallback(async () => {
      if (!effectiveTypeSlug) {
        setCounts({});
        return;
      }
      try {
        const rows = await getFilteredRepository().countByStatus();
        const map: Record<number, number> = {};
        for (const r of rows) map[r.lead_status_id] = r.count;
        setCounts(map);
      } catch (error) {
        logger.warn('FilterByStatus: countByStatus failed', error);
      }
    }, [getFilteredRepository, effectiveTypeSlug]);

    useEffect(() => {
      void reloadCounts();
      const unsubscribe = subscribe(() => {
        void reloadCounts();
      });
      return unsubscribe;
    }, [subscribe, reloadCounts]);

    // ── Восстановление из URL (один раз) ────────────────────────────────────
    useEffect(() => {
      try {
        const restored = parseFromUrl(fromUrl());
        if (restored.length > 0) {
          setSelected(restored);
          filter(repo => repo.filterByStatusAny(restored));
        }
      } catch (error) {
        logger.warn('FilterByStatus: fromUrl failed', error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (statusId: number) => {
      const next = selectedSet.has(statusId)
        ? selected.filter(id => id !== statusId)
        : [...selected, statusId];
      setSelected(next);
      filter(next.length > 0 ? repo => repo.filterByStatusAny(next) : null);
      toUrl(serializeForUrl(next));
    };

    // ── Render ──────────────────────────────────────────────────────────────
    // Воронка не определилась или у неё ещё нет статусов — фильтр не рендерим.
    if (!effectiveTypeSlug || statuses.length === 0) {
      return null;
    }

    return (
      <Group gap={4} wrap='wrap'>
        <Button.Group>
          {statuses.map(status => {
            const active = selectedSet.has(status.id);
            const count = counts[status.id] ?? 0;
            return (
              <Button
                key={status.id}
                size='xs'
                variant={active ? 'filled' : 'default'}
                onClick={() => handleClick(status.id)}
                disabled={disabled}
                leftSection={
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: status.color || '#3b82f6',
                      flexShrink: 0,
                    }}
                  />
                }
                rightSection={
                  <Badge size='xs' variant='light' color='gray'>
                    {count}
                  </Badge>
                }
              >
                {status.name}
              </Button>
            );
          })}
        </Button.Group>
      </Group>
    );
  }
);
